import { CONFIG } from '../config.js';
import { getIcon, formatMoney, showToast } from '../components/ui.js';
import { DataService } from '../services/data.js';

export async function renderSettingsView() {
  const [monthlyParams, companyProfile, webhookConfig, webhookLeadsConfig, emailTemplates, emailAccounts] = await Promise.all([
    DataService.getMonthlyParameters(),
    DataService.getCompanyProfile(),
    DataService.getWebhookConfig(),
    DataService.getWebhookLeadsConfig(),
    DataService.getEmailTemplates().catch(() => []),
    DataService.getEmailAccounts().catch(() => [])
  ]);
  const sortedMonths = Object.keys(monthlyParams).sort().reverse();
  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const currentParam = monthlyParams[currentMonthKey] || { fixed_cost: '', inflation_rate: '' };

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  return `
    <div class="space-y-6 max-w-4xl">
      <!-- Title -->
      <div class="pb-3 border-b border-border">
        <h1 class="text-2xl font-bold tracking-tight text-text-primary">Configuración del Sistema</h1>
        <p class="text-xs text-text-secondary mt-0.5">Parámetros generales de operación, costos fijos, inflación y conexiones</p>
      </div>

      <!-- Financial Monthly Parameters (Costos Fijos e Inflación) -->
      <div class="bg-surface p-5 rounded-md border border-border shadow-card space-y-5">
        <div class="pb-3 border-b border-border flex items-center justify-between">
          <div>
            <div class="flex items-center gap-2">
              <h3 class="font-bold text-sm text-text-primary">Parámetros Financieros Mensuales</h3>
              <span class="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold">
                Dashboard KPIs
              </span>
            </div>
            <p class="text-xs text-text-secondary mt-0.5">
              Carga manual del Costo Fijo mensual (para el punto de equilibrio) y la tasa de Inflación (para el cálculo del Valor Hora real)
            </p>
          </div>
        </div>

        <!-- Formulario de Carga / Edición -->
        <form id="form-monthly-params" class="bg-surface-subtle/60 p-4 rounded-md border border-border space-y-3">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Mes y Año (Período)</label>
              <input 
                type="month" 
                id="input-param-month" 
                value="${currentMonthKey}" 
                class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" 
                required 
              />
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Costo Fijo Mensual ($ ARS)</label>
              <input 
                type="number" 
                step="any" 
                min="0" 
                id="input-param-cost" 
                placeholder="Ej: 850000" 
                value="${currentParam.fixed_cost || ''}" 
                class="w-full text-xs font-mono font-bold border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" 
                required 
              />
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Inflación Mensual (%)</label>
              <input 
                type="number" 
                step="0.01" 
                id="input-param-inflation" 
                placeholder="Ej: 3.8" 
                value="${currentParam.inflation_rate || ''}" 
                class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" 
                required 
              />
            </div>
          </div>

          <div class="flex items-center justify-end gap-2 pt-1">
            <button 
              type="submit" 
              class="px-4 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active transition-all shadow-sm flex items-center gap-1.5"
            >
              ${getIcon('save', 'w-3.5 h-3.5')}
              <span>Guardar Parámetros del Mes</span>
            </button>
          </div>
        </form>

        <!-- Tabla Histórica de Costos e Inflación -->
        <div class="space-y-2">
          <div class="flex items-center justify-between">
            <span class="text-xs font-semibold text-text-secondary uppercase tracking-wider">Historial Registrado</span>
            <span class="text-[11px] font-mono text-text-secondary">${sortedMonths.length} meses configurados</span>
          </div>

          <div class="overflow-x-auto border border-border rounded-md">
            <table class="w-full text-left text-xs">
              <thead class="bg-surface-subtle border-b border-border text-text-secondary uppercase font-semibold">
                <tr>
                  <th class="px-4 py-2.5">Período</th>
                  <th class="px-4 py-2.5">Costo Fijo (ARS)</th>
                  <th class="px-4 py-2.5">Inflación Mensual</th>
                  <th class="px-4 py-2.5">Última Modificación</th>
                  <th class="px-4 py-2.5 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-border/60 font-medium">
                ${sortedMonths.length === 0 ? `
                  <tr>
                    <td colspan="5" class="px-4 py-6 text-center text-text-secondary">
                      No hay meses configurados todavía. Completa el formulario para registrar el primer período.
                    </td>
                  </tr>
                ` : sortedMonths.map(key => {
                  const item = monthlyParams[key];
                  const [y, mStr] = key.split('-');
                  const mIndex = parseInt(mStr, 10) - 1;
                  const label = `${monthNames[mIndex] || mStr} ${y}`;
                  return `
                    <tr class="hover:bg-surface-subtle/50 transition-colors">
                      <td class="px-4 py-2.5 font-bold text-text-primary font-mono">${label} (${key})</td>
                      <td class="px-4 py-2.5 font-mono font-bold text-emerald-700">${formatMoney(item.fixed_cost)}</td>
                      <td class="px-4 py-2.5 font-mono">${item.inflation_rate !== undefined ? `${item.inflation_rate}%` : '-'}</td>
                      <td class="px-4 py-2.5 font-mono text-text-secondary text-[11px]">
                        ${item.updated_at ? new Date(item.updated_at).toLocaleDateString('es-AR') : 'Inicial'}
                      </td>
                      <td class="px-4 py-2.5 text-right space-x-1">
                        <button 
                          class="btn-edit-param text-xs text-primary hover:underline font-semibold font-mono px-1.5 py-0.5"
                          data-month="${key}" 
                          data-cost="${item.fixed_cost || 0}" 
                          data-inflation="${item.inflation_rate || 0}"
                        >
                          Editar
                        </button>
                        <button 
                          class="btn-delete-param text-xs text-rose-600 hover:underline font-semibold font-mono px-1.5 py-0.5"
                          data-month="${key}"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Version & Release Info -->
      <div class="bg-surface p-5 rounded-md border border-border shadow-card space-y-4">
        <div class="flex items-center justify-between pb-3 border-b border-border">
          <div>
            <h3 class="font-bold text-sm text-text-primary">Versión de la Aplicación</h3>
            <p class="text-xs text-text-secondary">Control de versiones del despliegue en Cloudflare Pages</p>
          </div>
          <span class="text-xs font-mono font-bold px-2.5 py-1 rounded bg-primary text-white">
            v${CONFIG.VERSION}
          </span>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          <div class="p-3 bg-surface-subtle rounded border border-border">
            <span class="text-text-secondary block text-[10px] uppercase font-semibold">Entorno de Despliegue</span>
            <span class="font-bold font-mono text-text-primary mt-1 block">Cloudflare Pages (CI/CD Git)</span>
          </div>
          <div class="p-3 bg-surface-subtle rounded border border-border">
            <span class="text-text-secondary block text-[10px] uppercase font-semibold">Repositorio Git</span>
            <span class="font-bold font-mono text-text-primary mt-1 block">martinbat89/business-os</span>
          </div>
        </div>
      </div>

      <!-- Supabase Connection Config -->
      <div class="bg-surface p-5 rounded-md border border-border shadow-card space-y-4">
        <div class="flex items-center justify-between pb-3 border-b border-border">
          <div>
            <h3 class="font-bold text-sm text-text-primary">Base de Datos Supabase</h3>
            <p class="text-xs text-text-secondary">Infraestructura PostgreSQL y Supabase Auth</p>
          </div>
          <span class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-200">
            <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            Conectado
          </span>
        </div>

        <div class="space-y-2 text-xs font-mono">
          <div>
            <span class="text-[10px] uppercase font-semibold text-text-secondary">Project URL</span>
            <p class="p-2 bg-surface-subtle rounded border border-border text-text-primary mt-0.5 truncate">${CONFIG.SUPABASE_URL}</p>
          </div>
          <div>
            <span class="text-[10px] uppercase font-semibold text-text-secondary">Esquema Activo</span>
            <p class="p-2 bg-surface-subtle rounded border border-border text-text-primary mt-0.5">public (Tablas previas respaldadas en schema legacy_backup)</p>
          </div>
        </div>
      </div>

      <!-- Identidad y Contacto Institucional (Equipo Alerce) -->
      <div class="bg-surface p-5 rounded-md border border-border shadow-card space-y-4">
        <div class="pb-3 border-b border-border flex items-center justify-between">
          <div>
            <div class="flex items-center gap-2">
              <h3 class="font-bold text-sm text-text-primary">Identidad y Contacto Institucional (Equipo Alerce)</h3>
              <span class="text-[10px] font-mono px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-bold">
                Encabezados & Comprobantes
              </span>
            </div>
            <p class="text-xs text-text-secondary mt-0.5">
              Datos de contacto que se muestran en el encabezado de los presupuestos comerciales y remitos oficiales
            </p>
          </div>
        </div>

        <form id="form-company-profile" class="space-y-3">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Nombre Comercial</label>
              <input 
                type="text" 
                id="input-company-name" 
                value="${companyProfile.name || 'Equipo Alerce'}" 
                class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" 
                required 
              />
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Email Institucional</label>
              <input 
                type="email" 
                id="input-company-email" 
                placeholder="contacto@equipoalerce.com" 
                value="${companyProfile.email || ''}" 
                class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" 
              />
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Teléfono / WhatsApp</label>
              <input 
                type="text" 
                id="input-company-phone" 
                placeholder="+54 9 11 1234-5678" 
                value="${companyProfile.phone || ''}" 
                class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" 
              />
            </div>
          </div>

          <div class="flex justify-end pt-1">
            <button 
              type="submit" 
              class="px-4 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active transition-all shadow-sm flex items-center gap-1.5"
            >
              ${getIcon('save', 'w-3.5 h-3.5')}
              <span>Guardar Datos Institucionales</span>
            </button>
          </div>
        </form>
      </div>

      <!-- Integración Webhook (Landing Diagnóstico Equipo Alerce) -->
      <div class="bg-surface p-5 rounded-md border border-border shadow-card space-y-4">
        <div class="pb-3 border-b border-border flex items-center justify-between">
          <div>
            <div class="flex items-center gap-2">
              <h3 class="font-bold text-sm text-text-primary">Integración Webhook (Landing Diagnóstico)</h3>
              <span class="text-[10px] font-mono px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 font-bold">
                API Endpoint
              </span>
            </div>
            <p class="text-xs text-text-secondary mt-0.5">
              Recepción automática de contactos y respuestas del cuestionario web, creación de prospectos en el CRM y envío automático de confirmación con plantilla
            </p>
          </div>
        </div>

        <form id="form-webhook-config" class="space-y-4">
          <!-- Webhook Endpoint URL -->
          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">URL del Webhook (Edge Function)</label>
            <div class="flex items-center gap-2">
              <input 
                type="text" 
                id="input-webhook-url" 
                readonly 
                value="${CONFIG.SUPABASE_URL}/functions/v1/webhook-landing-diagnostic" 
                class="w-full text-xs font-mono p-2 bg-surface-subtle border border-border rounded-md text-text-primary select-all outline-none" 
              />
              <button 
                type="button" 
                id="btn-copy-webhook-url" 
                class="px-3 py-2 text-xs font-semibold border border-border rounded-md bg-surface hover:bg-surface-subtle text-text-primary shrink-0 flex items-center gap-1 shadow-sm"
              >
                ${getIcon('copy', 'w-3.5 h-3.5 text-text-secondary')}
                <span>Copiar URL</span>
              </button>
            </div>
            <span class="text-[11px] text-text-secondary mt-1 block">Método HTTP: <b>POST</b> • Formato: <b>JSON</b></span>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- API Key -->
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Clave de Seguridad (Header: x-alerce-api-key)</label>
              <div class="flex items-center gap-2">
                <input 
                  type="text" 
                  id="input-webhook-api-key" 
                  value="${webhookConfig.api_key || 'alerce-landing-diag-2026'}" 
                  class="w-full text-xs font-mono p-2 bg-surface border border-border rounded-md text-text-primary outline-none focus:ring-2 focus:ring-primary" 
                  required 
                />
                <button 
                  type="button" 
                  id="btn-copy-api-key" 
                  class="px-2.5 py-2 text-xs font-semibold border border-border rounded-md bg-surface hover:bg-surface-subtle text-text-primary shrink-0 flex items-center gap-1 shadow-sm"
                  title="Copiar API Key"
                >
                  ${getIcon('copy', 'w-3.5 h-3.5 text-text-secondary')}
                </button>
              </div>
            </div>

            <!-- Email account sender -->
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Cuenta Emisora para Notificaciones</label>
              <select id="select-webhook-account" class="w-full text-xs p-2 bg-surface border border-border rounded-md text-text-primary outline-none">
                <option value="">-- Sin cuenta asignada --</option>
                ${emailAccounts.map(a => `
                  <option value="${a.id}" ${String(webhookConfig.account_id) === String(a.id) ? 'selected' : ''}>
                    ${a.name} (${a.email_address})
                  </option>
                `).join('')}
              </select>
            </div>
          </div>

          <!-- Template selection & Toggle (Para el Cliente) -->
          <div class="p-3 bg-surface-subtle/70 rounded-md border border-border space-y-3">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <label class="block text-xs font-semibold text-text-primary">1. Email de Confirmación al Prospecto / Cliente</label>
                <p class="text-[11px] text-text-secondary">Se enviará a su casilla tras completar el cuestionario con sus resultados calculados</p>
              </div>
              <a href="#/email-marketing" class="text-xs text-primary font-semibold hover:underline inline-flex items-center gap-1">
                <span>Gestionar plantillas</span>
                ${getIcon('arrowRight', 'w-3 h-3')}
              </a>
            </div>

            <select id="select-webhook-template" class="w-full text-xs p-2 bg-surface border border-border rounded-md text-text-primary outline-none">
              <option value="">-- Seleccionar plantilla de diagnóstico --</option>
              ${emailTemplates.map(t => `
                <option value="${t.id}" ${String(webhookConfig.template_id) === String(t.id) ? 'selected' : ''}>
                  ${t.name} (Asunto: ${t.subject || 'Sin asunto'})
                </option>
              `).join('')}
            </select>

            <div class="flex items-center gap-2 pt-1">
              <input 
                type="checkbox" 
                id="check-webhook-send-email" 
                ${webhookConfig.send_email_enabled ? 'checked' : ''} 
                class="rounded border-border text-primary focus:ring-primary w-4 h-4 cursor-pointer" 
              />
              <label for="check-webhook-send-email" class="text-xs font-medium text-text-primary cursor-pointer select-none">
                Habilitar envío automático de email de confirmación al cliente
              </label>
            </div>
          </div>

          <!-- Notificaciones Internas al Operador (Para Martín Batarev) -->
          <div class="p-3.5 bg-surface-subtle/80 rounded-md border border-border/80 space-y-3">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <label class="block text-xs font-semibold text-text-primary flex items-center gap-1.5">
                  <span class="w-2 h-2 rounded-full bg-[#D2F26B]"></span>
                  <span>2. Notificación Instantánea al Operador (Equipo Alerce)</span>
                </label>
                <p class="text-[11px] text-text-secondary">Recibe un correo con el resumen y respuestas cada vez que entra un nuevo lead</p>
              </div>
              <span class="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
                Aviso a Dirección
              </span>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div>
                <label class="block text-[11px] font-semibold text-text-secondary mb-1">Email Destinatario del Operador</label>
                <input 
                  type="email" 
                  id="input-webhook-admin-email" 
                  value="${webhookConfig.admin_email_recipient || 'martin.batarev@gmail.com'}" 
                  placeholder="martin.batarev@gmail.com" 
                  class="w-full text-xs font-mono p-2 bg-surface border border-border rounded-md text-text-primary outline-none" 
                />
              </div>

              <div>
                <label class="block text-[11px] font-semibold text-text-secondary mb-1">Plantilla de Notificación (Requerida si está activo)</label>
                <select id="select-webhook-admin-template" class="w-full text-xs p-2 bg-surface border border-border rounded-md text-text-primary outline-none">
                  <option value="">-- Seleccionar plantilla de aviso al operador --</option>
                  ${emailTemplates.map(t => `
                    <option value="${t.id}" ${String(webhookConfig.admin_template_id) === String(t.id) ? 'selected' : ''}>
                      ${t.name}
                    </option>
                  `).join('')}
                </select>
              </div>
            </div>

            <div class="flex items-center gap-2 pt-1 border-t border-border/50">
              <input 
                type="checkbox" 
                id="check-webhook-notify-admin" 
                ${webhookConfig.notify_admin_enabled !== false ? 'checked' : ''} 
                class="rounded border-border text-primary focus:ring-primary w-4 h-4 cursor-pointer" 
              />
              <label for="check-webhook-notify-admin" class="text-xs font-semibold text-text-primary cursor-pointer select-none">
                Notificarme por email cuando se registre un nuevo lead vía webhook
              </label>
            </div>
          </div>

          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
            <details class="text-[11px] text-text-secondary">
              <summary class="cursor-pointer hover:text-text-primary font-semibold">Ver variables disponibles para la plantilla</summary>
              <div class="mt-2 p-2.5 bg-surface-subtle border border-border rounded font-mono text-[10px] space-y-1">
                <div><code>{{name}}</code>: Nombre del contacto</div>
                <div><code>{{business_name}}</code>: Empresa o marca</div>
                <div><code>{{diagnosis_category}}</code>: Categoría asignada</div>
                <div><code>{{diagnosis_summary}}</code>: Explicación resumida</div>
                <div><code>{{recommended_action}}</code>: Primer paso sugerido</div>
                <div><code>{{q1_problem}}</code>, <code>{{q2_current_method}}</code>, <code>{{q3_impact}}</code>, <code>{{q4_priority}}</code></div>
              </div>
            </details>

            <button 
              type="submit" 
              class="px-4 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active transition-all shadow-sm flex items-center gap-1.5 self-end"
            >
              ${getIcon('save', 'w-3.5 h-3.5')}
              <span>Guardar Configuración de Webhook</span>
            </button>
          </div>
        </form>
      </div>

      <!-- Integración Webhook (Leads Genéricos / WapaTienda & SaaS) -->
      <div class="bg-surface p-5 rounded-md border border-border shadow-card space-y-4">
        <div class="pb-3 border-b border-border flex items-center justify-between">
          <div>
            <div class="flex items-center gap-2">
              <h3 class="font-bold text-sm text-text-primary">Integración Webhook (Leads Genéricos / WapaTienda & SaaS)</h3>
              <span class="text-[10px] font-mono px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-bold">
                API Endpoint
              </span>
            </div>
            <p class="text-xs text-text-secondary mt-0.5">
              Recepción automática de contactos desde WapaTienda u otras fuentes SaaS, creación del cliente y oportunidad en el CRM, y despacho de emails con plantillas dinámicas
            </p>
          </div>
        </div>

        <form id="form-webhook-leads-config" class="space-y-4">
          <!-- Webhook Endpoint URL -->
          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">URL del Webhook (Edge Function)</label>
            <div class="flex items-center gap-2">
              <input 
                type="text" 
                id="input-webhook-leads-url" 
                readonly 
                value="${CONFIG.SUPABASE_URL}/functions/v1/webhook-leads" 
                class="w-full text-xs font-mono p-2 bg-surface-subtle border border-border rounded-md text-text-primary select-all outline-none" 
              />
              <button 
                type="button" 
                id="btn-copy-webhook-leads-url" 
                class="px-3 py-2 text-xs font-semibold border border-border rounded-md bg-surface hover:bg-surface-subtle text-text-primary shrink-0 flex items-center gap-1 shadow-sm"
              >
                ${getIcon('copy', 'w-3.5 h-3.5 text-text-secondary')}
                <span>Copiar URL</span>
              </button>
            </div>
            <span class="text-[11px] text-text-secondary mt-1 block">Método HTTP: <b>POST</b> • Formato: <b>JSON</b> • Cabecera de autenticación: <b>x-alerce-api-key</b></span>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- API Key -->
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Clave de Seguridad (Header: x-alerce-api-key)</label>
              <div class="flex items-center gap-2">
                <input 
                  type="text" 
                  id="input-webhook-leads-api-key" 
                  value="${webhookLeadsConfig.api_key || 'alerce-landing-diag-2026'}" 
                  class="w-full text-xs font-mono p-2 bg-surface border border-border rounded-md text-text-primary outline-none focus:ring-2 focus:ring-primary" 
                  required 
                />
                <button 
                  type="button" 
                  id="btn-copy-leads-api-key" 
                  class="px-2.5 py-2 text-xs font-semibold border border-border rounded-md bg-surface hover:bg-surface-subtle text-text-primary shrink-0 flex items-center gap-1 shadow-sm"
                  title="Copiar API Key"
                >
                  ${getIcon('copy', 'w-3.5 h-3.5 text-text-secondary')}
                </button>
              </div>
            </div>

            <!-- Email account sender -->
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Cuenta Emisora para Emails</label>
              <select id="select-webhook-leads-account" class="w-full text-xs p-2 bg-surface border border-border rounded-md text-text-primary outline-none">
                <option value="">-- Sin cuenta asignada --</option>
                ${emailAccounts.map(a => `
                  <option value="${a.id}" ${String(webhookLeadsConfig.account_id) === String(a.id) ? 'selected' : ''}>
                    ${a.name} (${a.email_address})
                  </option>
                `).join('')}
              </select>
            </div>
          </div>

          <!-- Template selection & Toggle (Para el Lead) -->
          <div class="p-3 bg-surface-subtle/70 rounded-md border border-border space-y-3">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <label class="block text-xs font-semibold text-text-primary">1. Email de Confirmación al Lead (Contacto)</label>
                <p class="text-[11px] text-text-secondary">Se enviará a su casilla cuando complete el formulario en WapaTienda o la landing externa</p>
              </div>
              <a href="#/email-marketing" class="text-xs text-primary font-semibold hover:underline inline-flex items-center gap-1">
                <span>Gestionar plantillas</span>
                ${getIcon('arrowRight', 'w-3 h-3')}
              </a>
            </div>

            <select id="select-webhook-leads-template" class="w-full text-xs p-2 bg-surface border border-border rounded-md text-text-primary outline-none">
              <option value="">-- Sin plantilla (Desactivado) --</option>
              ${emailTemplates.map(t => `
                <option value="${t.id}" ${String(webhookLeadsConfig.lead_template_id) === String(t.id) ? 'selected' : ''}>
                  ${t.name} (Asunto: ${t.subject || 'Sin asunto'})
                </option>
              `).join('')}
            </select>

            <div class="flex items-center gap-2 pt-1">
              <input 
                type="checkbox" 
                id="check-webhook-leads-send-email" 
                ${webhookLeadsConfig.send_lead_email_enabled ? 'checked' : ''} 
                class="rounded border-border text-primary focus:ring-primary w-4 h-4 cursor-pointer" 
              />
              <label for="check-webhook-leads-send-email" class="text-xs font-medium text-text-primary cursor-pointer select-none">
                Habilitar envío automático de confirmación por email al lead
              </label>
            </div>

            <details class="text-[11px] text-text-secondary pt-1">
              <summary class="cursor-pointer hover:text-text-primary font-semibold text-[11px] text-primary">Ver variables disponibles para la plantilla del lead</summary>
              <div class="mt-2 p-2.5 bg-surface border border-border rounded font-mono text-[10px] space-y-1 text-text-secondary">
                <div><code>{{name}}</code>: Nombre completo enviado</div>
                <div><code>{{first_name}}</code>: Primer nombre (ej: "Juan")</div>
                <div><code>{{business_name}}</code> o <code>{{company}}</code>: Nombre del negocio / marca</div>
                <div><code>{{email}}</code>: Email del lead</div>
                <div><code>{{phone}}</code> o <code>{{whatsapp}}</code>: Teléfono o WhatsApp</div>
                <div><code>{{category}}</code> o <code>{{rubro}}</code>: Rubro o actividad comercial</div>
                <div><code>{{channel}}</code>: Canal de origen (ej: "WapaTienda")</div>
                <div><code>{{notes}}</code> o <code>{{message}}</code>: Mensaje o notas enviadas</div>
                <div><code>{{cta}}</code>: Inserta el botón con enlace configurado en la plantilla</div>
              </div>
            </details>
          </div>

          <!-- Notificaciones Internas al Operador (Para Martín Batarev) -->
          <div class="p-3.5 bg-surface-subtle/80 rounded-md border border-border/80 space-y-3">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <label class="block text-xs font-semibold text-text-primary flex items-center gap-1.5">
                  <span class="w-2 h-2 rounded-full bg-blue-500"></span>
                  <span>2. Notificación Instantánea al Operador (Aviso Interno)</span>
                </label>
                <p class="text-[11px] text-text-secondary">Recibe un correo con los datos del lead y un botón de acceso directo al CRM</p>
              </div>
              <span class="text-[10px] font-mono px-2 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200">
                Aviso a Dirección
              </span>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div>
                <label class="block text-[11px] font-semibold text-text-secondary mb-1">Email Destinatario del Operador</label>
                <input 
                  type="email" 
                  id="input-webhook-leads-admin-email" 
                  value="${webhookLeadsConfig.admin_email_recipient || 'martin.batarev@gmail.com'}" 
                  placeholder="martin.batarev@gmail.com" 
                  class="w-full text-xs font-mono p-2 bg-surface border border-border rounded-md text-text-primary outline-none" 
                />
              </div>

              <div>
                <label class="block text-[11px] font-semibold text-text-secondary mb-1">Plantilla de Notificación (Opcional)</label>
                <select id="select-webhook-leads-admin-template" class="w-full text-xs p-2 bg-surface border border-border rounded-md text-text-primary outline-none">
                  <option value="">-- Por defecto (Ficha técnica con botón al CRM) --</option>
                  ${emailTemplates.map(t => `
                    <option value="${t.id}" ${String(webhookLeadsConfig.admin_template_id) === String(t.id) ? 'selected' : ''}>
                      ${t.name}
                    </option>
                  `).join('')}
                </select>
              </div>
            </div>

            <div class="flex items-center gap-2 pt-1 border-t border-border/50">
              <input 
                type="checkbox" 
                id="check-webhook-leads-notify-admin" 
                ${webhookLeadsConfig.notify_admin_enabled !== false ? 'checked' : ''} 
                class="rounded border-border text-primary focus:ring-primary w-4 h-4 cursor-pointer" 
              />
              <label for="check-webhook-leads-notify-admin" class="text-xs font-semibold text-text-primary cursor-pointer select-none">
                Notificarme por email cuando ingrese un nuevo lead vía webhook-leads
              </label>
            </div>

            <details class="text-[11px] text-text-secondary pt-1">
              <summary class="cursor-pointer hover:text-text-primary font-semibold text-[11px] text-primary">Ver variables disponibles para la notificación al operador</summary>
              <div class="mt-2 p-2.5 bg-surface border border-border rounded font-mono text-[10px] space-y-1 text-text-secondary">
                <div><code>{{name}}</code>, <code>{{first_name}}</code>, <code>{{email}}</code>, <code>{{business_name}}</code>, <code>{{phone}}</code>, <code>{{category}}</code>, <code>{{channel}}</code>, <code>{{notes}}</code></div>
                <div><code>{{submitted_at}}</code>: Fecha y hora exacta de recepción</div>
                <div><code>{{client_id}}</code>: ID numérico del cliente en el CRM</div>
                <div><code>{{crm_link}}</code>: Enlace directo al cliente en Business OS</div>
              </div>
            </details>
          </div>

          <div class="flex justify-end pt-2">
            <button 
              type="submit" 
              class="px-4 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active transition-all shadow-sm flex items-center gap-1.5"
            >
              ${getIcon('save', 'w-3.5 h-3.5')}
              <span>Guardar Configuración de Webhook Leads</span>
            </button>
          </div>
        </form>
      </div>

      <!-- Business Parameters -->
      <div class="bg-surface p-5 rounded-md border border-border shadow-card space-y-4">
        <div class="pb-3 border-b border-border">
          <h3 class="font-bold text-sm text-text-primary">Perfil del Operador</h3>
          <p class="text-xs text-text-secondary">Datos del solopreneur</p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Nombre</label>
            <input type="text" value="${CONFIG.OWNER}" disabled class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface-subtle text-text-secondary" />
          </div>
          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Email</label>
            <input type="email" value="${CONFIG.OWNER_EMAIL}" disabled class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface-subtle text-text-secondary" />
          </div>
        </div>
      </div>
    </div>
  `;
}

export function initSettingsEvents() {
  const formProfile = document.getElementById('form-company-profile');
  if (formProfile) {
    formProfile.onsubmit = async (e) => {
      e.preventDefault();
      const name = document.getElementById('input-company-name')?.value || 'Equipo Alerce';
      const email = document.getElementById('input-company-email')?.value || '';
      const phone = document.getElementById('input-company-phone')?.value || '';

      try {
        await DataService.saveCompanyProfile({ name, email, phone });
        showToast('Datos de contacto institucional de Equipo Alerce actualizados');
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (err) {
        showToast(`Error al guardar: ${err.message}`, 'error');
      }
    };
  }

  const form = document.getElementById('form-monthly-params');
  const inputMonth = document.getElementById('input-param-month');
  const inputCost = document.getElementById('input-param-cost');
  const inputInflation = document.getElementById('input-param-inflation');

  if (form) {
    form.onsubmit = async (e) => {
      e.preventDefault();
      const monthKey = inputMonth.value.trim();
      const fixedCost = parseFloat(inputCost.value) || 0;
      const inflationRate = parseFloat(inputInflation.value) || 0;

      if (!monthKey) {
        showToast('Debes seleccionar un mes y año válidos', 'error');
        return;
      }

      try {
        await DataService.saveMonthlyParameter(monthKey, { fixedCost, inflationRate });
        showToast(`Parámetros para ${monthKey} guardados correctamente`);
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (err) {
        showToast(`Error al guardar: ${err.message}`, 'error');
      }
    };
  }

  // Botones de editar
  document.querySelectorAll('.btn-edit-param').forEach(btn => {
    btn.onclick = () => {
      const month = btn.getAttribute('data-month');
      const cost = btn.getAttribute('data-cost');
      const inflation = btn.getAttribute('data-inflation');

      if (inputMonth) inputMonth.value = month;
      if (inputCost) inputCost.value = cost;
      if (inputInflation) inputInflation.value = inflation;

      inputCost?.focus();
      showToast(`Cargado período ${month} en el formulario`);
    };
  });

  // Botones de eliminar
  document.querySelectorAll('.btn-delete-param').forEach(btn => {
    btn.onclick = async () => {
      const month = btn.getAttribute('data-month');
      if (!confirm(`¿Eliminar los parámetros guardados para ${month}?`)) return;

      try {
        await DataService.deleteMonthlyParameter(month);
        showToast(`Parámetros de ${month} eliminados`);
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (err) {
        showToast(`Error al eliminar: ${err.message}`, 'error');
      }
    };
  });

  // --- Eventos de Configuración de Webhook de Landing ---
  const formWebhook = document.getElementById('form-webhook-config');
  if (formWebhook) {
    formWebhook.onsubmit = async (e) => {
      e.preventDefault();
      const apiKey = document.getElementById('input-webhook-api-key')?.value.trim() || 'alerce-landing-diag-2026';
      const accountId = document.getElementById('select-webhook-account')?.value || null;
      const templateId = document.getElementById('select-webhook-template')?.value || null;
      const sendEmailEnabled = document.getElementById('check-webhook-send-email')?.checked || false;

      const adminEmailRecipient = document.getElementById('input-webhook-admin-email')?.value.trim() || 'martin.batarev@gmail.com';
      const adminTemplateId = document.getElementById('select-webhook-admin-template')?.value || null;
      const notifyAdminEnabled = document.getElementById('check-webhook-notify-admin')?.checked !== false;

      if (sendEmailEnabled && (!templateId || !accountId)) {
        showToast('Para habilitar el envío automático al cliente, debes seleccionar una plantilla y una cuenta remitente.', 'error');
        return;
      }

      if (notifyAdminEnabled && (!adminTemplateId || !adminEmailRecipient)) {
        showToast('Para recibir la notificación de nuevo lead, debes seleccionar una plantilla de notificación y especificar tu correo.', 'error');
        return;
      }

      try {
        await DataService.saveWebhookConfig({
          api_key: apiKey,
          account_id: accountId,
          template_id: templateId,
          send_email_enabled: sendEmailEnabled,
          admin_email_recipient: adminEmailRecipient,
          admin_template_id: adminTemplateId,
          notify_admin_enabled: notifyAdminEnabled
        });
        showToast('Configuración del Webhook guardada exitosamente');
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (err) {
        showToast(`Error guardando configuración: ${err.message}`, 'error');
      }
    };
  }

  // Copiar URL del Webhook
  const btnCopyUrl = document.getElementById('btn-copy-webhook-url');
  if (btnCopyUrl) {
    btnCopyUrl.onclick = async () => {
      const url = document.getElementById('input-webhook-url')?.value;
      if (url) {
        await navigator.clipboard.writeText(url);
        showToast('URL del Webhook copiada al portapapeles');
      }
    };
  }

  // Copiar API Key
  const btnCopyKey = document.getElementById('btn-copy-api-key');
  if (btnCopyKey) {
    btnCopyKey.onclick = async () => {
      const key = document.getElementById('input-webhook-api-key')?.value;
      if (key) {
        await navigator.clipboard.writeText(key);
        showToast('Clave de API copiada al portapapeles');
      }
    };
  }

  // --- Eventos de Configuración de Webhook de Leads Genéricos (WapaTienda / SaaS) ---
  const formWebhookLeads = document.getElementById('form-webhook-leads-config');
  if (formWebhookLeads) {
    formWebhookLeads.onsubmit = async (e) => {
      e.preventDefault();
      const apiKey = document.getElementById('input-webhook-leads-api-key')?.value.trim() || 'alerce-landing-diag-2026';
      const accountId = document.getElementById('select-webhook-leads-account')?.value || null;
      const leadTemplateId = document.getElementById('select-webhook-leads-template')?.value || null;
      const sendLeadEmailEnabled = document.getElementById('check-webhook-leads-send-email')?.checked || false;

      const adminEmailRecipient = document.getElementById('input-webhook-leads-admin-email')?.value.trim() || 'martin.batarev@gmail.com';
      const adminTemplateId = document.getElementById('select-webhook-leads-admin-template')?.value || null;
      const notifyAdminEnabled = document.getElementById('check-webhook-leads-notify-admin')?.checked !== false;

      if (sendLeadEmailEnabled && (!leadTemplateId || !accountId)) {
        showToast('Para habilitar el envío automático al lead, debes seleccionar una plantilla de confirmación y una cuenta remitente.', 'error');
        return;
      }

      if (notifyAdminEnabled && !adminEmailRecipient) {
        showToast('Para recibir la notificación de nuevo lead, debes especificar tu correo de operador.', 'error');
        return;
      }

      try {
        await DataService.saveWebhookLeadsConfig({
          api_key: apiKey,
          account_id: accountId,
          lead_template_id: leadTemplateId,
          send_lead_email_enabled: sendLeadEmailEnabled,
          admin_email_recipient: adminEmailRecipient,
          admin_template_id: adminTemplateId,
          notify_admin_enabled: notifyAdminEnabled
        });
        showToast('Configuración del Webhook de Leads guardada exitosamente');
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (err) {
        showToast(`Error guardando configuración: ${err.message}`, 'error');
      }
    };
  }

  // Copiar URL del Webhook Leads
  const btnCopyLeadsUrl = document.getElementById('btn-copy-webhook-leads-url');
  if (btnCopyLeadsUrl) {
    btnCopyLeadsUrl.onclick = async () => {
      const url = document.getElementById('input-webhook-leads-url')?.value;
      if (url) {
        await navigator.clipboard.writeText(url);
        showToast('URL del Webhook de Leads copiada al portapapeles');
      }
    };
  }

  // Copiar API Key Leads
  const btnCopyLeadsKey = document.getElementById('btn-copy-leads-api-key');
  if (btnCopyLeadsKey) {
    btnCopyLeadsKey.onclick = async () => {
      const key = document.getElementById('input-webhook-leads-api-key')?.value;
      if (key) {
        await navigator.clipboard.writeText(key);
        showToast('Clave de API copiada al portapapeles');
      }
    };
  }
}

